// HEAL-44a — как показывать загрузку: название, статус, размер, дата.
//
// Вынесено из UploadHistory, потому что ровно то же самое нужно экрану анализа.
// Две копии подписей статусов на соседних экранах гарантированно разъедутся.

import type { UploadFailureReason, UploadListItem, UploadStatus } from '@/api/types';

export type StatusTone = 'ok' | 'warn' | 'wait' | 'hold' | 'err';

// HEAL-23: «Ждёт проверки» закреплено за `reported` — это ожидание НАШЕЙ ручной
// проверки после жалобы. `awaiting_user_review` (ждём действия человека) стал
// «Нужно подтвердить», иначе две разные вещи назывались бы одинаково.
export const STATUS_LABEL: Record<UploadStatus, { text: string; tone: StatusTone }> = {
  success: { text: 'ОК', tone: 'ok' },
  awaiting_user_review: { text: 'Нужно подтвердить', tone: 'warn' },
  processing: { text: 'Обрабатывается', tone: 'wait' },
  failed: { text: 'Ошибка', tone: 'err' },
  reported: { text: 'Ждёт проверки', tone: 'hold' },
};

// HEAL-29: подписи разведены по смыслу — раньше «файл не прочитался» и «это не
// анализ» звучали одинаково, и человек перезагружал тот же нечитаемый файл.
export const FAILURE_HINT: Record<UploadFailureReason, string> = {
  corrupted: 'Файл повреждён',
  not_a_lab_report: 'Не нашли результатов анализов',
  unsupported_analysis_type: 'Тип исследования не поддерживаем',
  parser_response_invalid: 'Не удалось разобрать ответ',
  parser_unavailable: 'Сервис временно недоступен',
  openai_error_terminal: 'Сервис временно недоступен',
  needs_ocr_review: 'Не удалось прочитать файл',
};

/**
 * Единственный порядок предпочтения на весь продукт.
 *
 * `title` пуст у загрузок до выката HEAL-44 и у тех, где сматчить оказалось
 * нечего: там показываем имя файла — ровно то же, что было видно раньше.
 */
export function uploadTitle(upload: Pick<UploadListItem, 'title' | 'original_filename'>): string {
  return upload.title ?? upload.original_filename;
}

export function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} МБ`;
}

export function formatDate(dateStr: string): string {
  const d = new Date(dateStr);
  return d.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

const MARKER_FORMS = ['показатель', 'показателя', 'показателей'] as const;

/** Зеркало UploadTitleGenerator::pluralizeMarkers — тексты должны совпадать. */
export function pluralMarkers(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return MARKER_FORMS[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return MARKER_FORMS[1];
  return MARKER_FORMS[2];
}
